"use client";

import Image from "next/image";
import { Controller, useForm } from "react-hook-form";
import cx from "classnames";
import { Link } from "@/i18n/navigation";
import styles from "./index.module.scss";
import Promo from "./promo";
import { useCallback, useState } from "react";
import { useMediaQuery } from "@mui/material";
import { useAuthRegister } from "@/domains/auth/hooks/user-auth-register";
import { RegistrationField, RegistrationProps } from "../../types";
import { PhoneInput } from "@/components/UI/PhoneInput/PhoneInput";
import { useTranslations } from "next-intl";
import { countryToCurrency } from "@/utils/country-to-currency";
import { useStore } from "@/hooks/use-store";

export default function Registration({ onShowTab }: RegistrationProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
  } = useForm<RegistrationField>({
    reValidateMode: "onSubmit",
    mode: "all",
  });
  const t = useTranslations("AuthRegistration");
  const tValidation = useTranslations("Validation");
  const isTabletOrMobile = useMediaQuery("only screen and (max-width: 1120px)");
  const [isOpenBonus, setIsOpenBonus] = useState(false);
  const [promoId, setPromoId] = useState<string | null>(null);
  const [country, setCountry] = useState<string>("UA");
  const [errorMessage, setErrorMessage] = useState("");
  const { loading, onMutateAuth, renderErrorMessage, error: authError } = useAuthRegister();
  const { user } = useStore();

  const openSheetBonus = () => setIsOpenBonus(true);
  const closeSheetBonus = () => setIsOpenBonus(false);

  const handleCountryChange = (value: string) => {
    if (value) {
      setCountry(value);
    }
  };

  const onSubmit = useCallback(
    async (data: RegistrationField) => {
      setErrorMessage("");
      try {
        const cleanedPhone = data.phoneInput.replace("+", "");

        const currency = countryToCurrency(country.toUpperCase());

        const result = await onMutateAuth({
          ...data,
          phoneInput: cleanedPhone,
          country: country.toUpperCase(),
          currency,
          promoId,
          utmParams: user.getUtmParams(),
        });

        if (!result?.success || !result?.code) return;

        user.setUtmParams(null);

        onShowTab("otpVerification");
      } catch (error: any) {
        setErrorMessage(error.message);
      }
    },
    [onMutateAuth, onShowTab],
  );

  return (
    <>
      <div className={styles.main_wrapper}>
        <div className={styles.form_wrapper}>
          <div className={styles.header}>
            <div className={styles.title}>{t("registration")}</div>

            <button className={styles.state_btn} onClick={() => onShowTab("login")}>
              <svg
                data-v-4e9cc203=""
                viewBox="0 0 20 18"
                fill="currentColor"
                width="18"
                height="100%"
                className={styles.state_btn__icon}
              >
                <use href="/images/sprite.svg#login"></use>
              </svg>
              {t("login")}
            </button>
          </div>

          {isTabletOrMobile && (
            <div className={styles.welcome_bonus}>
              <div className={styles.welcome_bonus__box}>
                <div className={styles.welcome_bonus___wrapper}>
                  <Image
                    className={styles.welcome_bonus__img}
                    src={"/images/bonus.png"}
                    width={132}
                    height={132}
                    alt="bonus"
                  />
                </div>
              </div>

              <div className={styles.welcome_bonus__title}>
                <span>{t("select_bonus")}</span>
                <Image
                  className={styles.welcome_bonus__title_icon}
                  src="/images/icons/24-arrow.svg"
                  alt=""
                  width={145}
                  height={50}
                />
              </div>

              <div className={styles.welcome_bonus__box}>
                <div className={styles.welcome_bonus__img_wrapper}>
                  <Image
                    className={styles.welcome_bonus__img}
                    src={"/images/bonus.png"}
                    width={132}
                    height={132}
                    alt="bonus"
                  />
                </div>
              </div>

              <button className={styles.welcome_bonus__btn} onClick={openSheetBonus}></button>
            </div>
          )}

          <form
            className={styles.form}
            onSubmit={(e) => {
              handleSubmit(onSubmit)(e);
            }}
          >
            <div className={styles.form_field}>
              <label className={cx(styles.label, styles.phone_label)}>
                <Controller
                  name="phoneInput"
                  control={control}
                  rules={{ required: tValidation("required") }}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <PhoneInput
                        autoComplete="off"
                        defaultCountry={country}
                        className={styles.phone_input}
                        value={value}
                        onChange={(_phone, _country) => {
                          onChange(_phone);
                          handleCountryChange(_country);
                        }}
                        label={false}
                      />
                    </>
                  )}
                />
              </label>
              {errors.phoneInput && (
                <div className={styles.message}>{errors.phoneInput.message}</div>
              )}
              <div className={styles.message}>{renderErrorMessage("phone", authError || [])}</div>
            </div>

            <div className={styles.form_field}>
              <label className={styles.label}>
                <input
                  {...register("password", {
                    required: tValidation("required"),
                    minLength: {
                      value: 6,
                      message: tValidation("password_min_length"),
                    },
                  })}
                  className={styles.input}
                  type="password"
                  placeholder={""}
                />
                <span className={styles.label_value}>{t("password")}</span>
              </label>
              {errors.password && <div className={styles.message}>{errors.password.message}</div>}
              <div className={styles.message}>
                {renderErrorMessage("password", authError || [])}
              </div>
            </div>
            {errorMessage && <div className={styles.error_message}>{errorMessage}</div>}
            <div className={styles.form_field}>
              <button className={styles.btn_submit} type="submit">
                {t("submit")}
                {loading && "..."}
              </button>
            </div>

            <div className={styles.form_field}>
              <label className={styles.label_checkbox}>
                <input className={styles.checkbox} type="checkbox" {...register("over18")} />
                <div className={styles.checkbox_pseudo}></div>
                <span className={styles.description}>
                  {t.rich("rulesDesk", {
                    link_1: (chunks) => (
                      <Link href={"/"} target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </Link>
                    ),
                    link_2: (chunks) => (
                      <Link href={"/"} target="_blank" rel="noopener noreferrer">
                        {chunks}
                      </Link>
                    ),
                  })}
                </span>
              </label>

              {errors.over18 && <div className={styles.message}>{errors.over18.message}</div>}
            </div>

            {/* <div className={styles.form_field}>
              <label className={styles.label_checkbox}>
                <input className={styles.checkbox} type="checkbox" {...register("subscription")} />
                <div className={styles.checkbox_pseudo}></div>
                <span className={styles.description}>Получать информацию об акциях и бонусах</span>
              </label>
            </div> */}
          </form>
        </div>

        <Promo
          setPromoId={setPromoId}
          promoId={promoId}
          isOpen={isOpenBonus}
          onClose={closeSheetBonus}
          country={country}
        />
      </div>
    </>
  );
}
